/**
 * Hook for syncing backend comments INTO Word as native Word comments.
 *
 * This is the reverse direction of useWordCommentSync (Word → Backend).
 * When a comment is created in the web app or mobile, it arrives via SSE,
 * TanStack Query refetches, and this hook inserts it as a Word comment.
 *
 * Requires WordApi 1.4 for Range.insertComment().
 * Microsoft 365 only — does NOT work on Office 2019/2021.
 *
 * Dedup: Comments created from Word already have word_cc_tag set — those
 * are skipped. Only "external" comments (no word_cc_tag) are synced.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { devLog } from '../services/devLog'
import type { NegotiationComment } from '../services/negotiationsApi'

interface UseBackendToWordSyncOptions {
  negotiationId: number
  comments: NegotiationComment[]
  enabled?: boolean
}

interface BackendToWordSyncState {
  isSupported: boolean
  syncedCount: number
}

const STORAGE_PREFIX = 'kobsign-backend-word-sync-'

function isInsertCommentSupported(): boolean {
  try {
    return Office.context.requirements.isSetSupported('WordApi', '1.4')
  } catch {
    return false
  }
}

/** Load synced comment IDs from localStorage */
function loadSyncedIds(negotiationId: number): Set<number> {
  try {
    const key = `${STORAGE_PREFIX}${negotiationId}`
    const raw = localStorage.getItem(key)
    if (!raw) return new Set()
    return new Set(JSON.parse(raw) as number[])
  } catch {
    return new Set()
  }
}

/** Persist synced comment IDs to localStorage */
function saveSyncedIds(negotiationId: number, ids: Set<number>) {
  try {
    const key = `${STORAGE_PREFIX}${negotiationId}`
    localStorage.setItem(key, JSON.stringify([...ids]))
  } catch {
    // Best-effort persistence
  }
}

/** Format comment text for Word comment bubble */
function formatCommentText(comment: NegotiationComment): string {
  const author = comment.author?.display_name || '?'
  let tag = ''
  if (comment.visibility === 'SPECIFIC' && comment.visible_to.length > 0) {
    tag = ` [→${comment.visible_to[0].tag_name}]`
  } else if (comment.visibility === 'INTERNAL') {
    tag = ' [Internal]'
  }
  return `${author}${tag}: ${comment.content}`
}

export function useBackendToWordSync({
  negotiationId,
  comments,
  enabled = true,
}: UseBackendToWordSyncOptions): BackendToWordSyncState {
  const supported = useRef(isInsertCommentSupported()).current
  const [syncedCount, setSyncedCount] = useState(0)
  const syncedIdsRef = useRef<Set<number>>(new Set())
  const isProcessingRef = useRef(false)
  const initializedRef = useRef(false)

  // Load persisted synced IDs on mount
  useEffect(() => {
    syncedIdsRef.current = loadSyncedIds(negotiationId)
    setSyncedCount(syncedIdsRef.current.size)
    initializedRef.current = true
  }, [negotiationId])

  const syncComment = useCallback(async (comment: NegotiationComment): Promise<boolean> => {
    const anchor = comment.anchors?.[0]
    if (!anchor) {
      // No anchor — can't place in document, skip
      devLog.info('BackendToWordSync', `Comment ${comment.id} has no anchor, skipping`)
      return false
    }

    try {
      await Word.run(async (context) => {
        let targetRange: Word.Range | null = null

        // Strategy 1: Search for anchor text in document
        if (anchor.anchor_text) {
          const searchResults = context.document.body.search(anchor.anchor_text, {
            matchCase: false,
            matchWholeWord: false,
          })
          searchResults.load('items')
          await context.sync()

          if (searchResults.items.length > 0) {
            targetRange = searchResults.items[0]
          }
        }

        // Strategy 2: Fallback to paragraph index
        if (!targetRange && anchor.anchor_paragraph != null) {
          const paragraphs = context.document.body.paragraphs
          paragraphs.load('items')
          await context.sync()

          const idx = anchor.anchor_paragraph - 1 // 1-indexed to 0-indexed
          if (idx >= 0 && idx < paragraphs.items.length) {
            targetRange = paragraphs.items[idx].getRange()
          }
        }

        if (!targetRange) {
          devLog.warn('BackendToWordSync', `Could not find anchor for comment ${comment.id}`)
          return
        }

        // Insert as native Word comment
        const text = formatCommentText(comment)
        targetRange.insertComment(text)
        await context.sync()
      })

      devLog.info('BackendToWordSync', `Synced comment ${comment.id} to Word`)
      return true
    } catch (err) {
      devLog.warn('BackendToWordSync', `Failed to sync comment ${comment.id}: ${err}`)
      return false
    }
  }, [])

  // Process new comments when list changes
  useEffect(() => {
    if (!enabled || !supported || !initializedRef.current || isProcessingRef.current) return
    if (!comments || comments.length === 0) return

    // Find comments that need syncing:
    // 1. Not already synced (not in syncedIds)
    // 2. word_cc_tag is empty/null (not created from Word — avoid reverse-syncing)
    // 3. Not system comments
    // 4. Not deleted
    const newComments = comments.filter(
      (c) =>
        !syncedIdsRef.current.has(c.id) &&
        !c.word_cc_tag &&
        c.comment_type === 'USER' &&
        !c.is_deleted,
    )

    if (newComments.length === 0) return

    isProcessingRef.current = true

    const processComments = async () => {
      for (const comment of newComments) {
        await syncComment(comment)
        // Mark as synced regardless of success — avoid infinite retries
        syncedIdsRef.current.add(comment.id)
      }
      saveSyncedIds(negotiationId, syncedIdsRef.current)
      setSyncedCount(syncedIdsRef.current.size)
      isProcessingRef.current = false
    }

    processComments()
  }, [comments, enabled, supported, negotiationId, syncComment])

  return { isSupported: supported, syncedCount }
}
